import React, { useState, useEffect } from 'react'
import { Box, Typography } from '@mui/joy'
import { ClientLoadModel } from '@numeo/types-core'
import { HorizontalDirectionIcon } from 'assets/icons/DatDirectionIcons'
import { DTMFKeypadRef } from './DTMFKeypad'
import { RateColumn } from 'components/shared/LoadsTable/RateColumn'
import { CompanyColumn } from 'components/shared/LoadsTable/CompanyColumn'
import { SendLoadInfoButton } from 'components/shared/LoadsTable/MapAndCosts/SendLoadInfoButton'
import { TripColumn } from 'components/shared/LoadsTable/TripColumn'
import { useTrucksByIds } from '@/hooks/fleet/useTrucksByIds'

interface LoadDetailsProps {
    load: ClientLoadModel
    dtmfKeypadRef?: React.RefObject<DTMFKeypadRef>
    truckId?: string
}

export const LoadDetails: React.FC<LoadDetailsProps> = ({ load, dtmfKeypadRef, truckId }) => {
    const [duration, setDuration] = useState<number | undefined>(load?.duration || undefined)

    // The call's truck, by id from the trucks cache
    const { trucks } = useTrucksByIds([truckId])
    const truck = truckId ? (trucks.get(String(truckId)) ?? null) : null

    // Calculate deadhead miles from load data
    const calculateDeadheadMiles = () => {
        const originMiles = load?.originDeadheadMiles?.miles || 0
        const destMiles = load?.destinationDeadheadMiles?.miles || 0
        return originMiles + destMiles
    }

    // Set duration from load data
    useEffect(() => {
        setDuration(load?.duration || undefined)
    }, [load?.duration])
    const renderLocationDetails = () => {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', p: 1 }}>
                <Typography
                    level="title-lg"
                    sx={{
                        fontSize: 20,
                        fontWeight: 'bold',
                        display: 'flex',
                        gap: 2,
                        alignItems: 'center',
                    }}
                >
                    {load.origin.city}
                    <HorizontalDirectionIcon />
                    {load.destination.city}
                </Typography>
                <Typography
                    level="body-sm"
                    sx={{
                        fontSize: '1.3rem',
                        fontWeight: 'bold',
                    }}
                >
                    {' '}
                    {load.tripLength && `${load.tripLength} ml`}
                </Typography>
            </Box>
        )
    }

    return (
        <Box>
            <Typography level="title-md">Load Details</Typography>

            {/* Pickup and Delivery Details */}
            {renderLocationDetails()}

            {/* Trip Column - First Row */}
            <Box sx={{ mt: 1, width: '100%' }}>
                <TripColumn load={load} loadSearchId={load._id} dtmfKeypadRef={dtmfKeypadRef} />
            </Box>

            {/* Rate Column - Second Row */}
            <Box sx={{ mt: 3, width: '100%' }}>
                <RateColumn
                    load={{
                        ...load,
                        posterInfo: {
                            ...load?.posterInfo,
                            contact: {
                                ...load?.posterInfo?.contact,
                                email: load?.posterInfo?.contact?.email || '',
                            },
                        },
                    }}
                />
            </Box>

            {/* Company Column - Third Row */}
            <Box sx={{ mt: 3, width: '100%' }}>
                <CompanyColumn load={load} />
            </Box>

            {/* Send Load Info Button */}
            {truck && (
                <Box
                    sx={{
                        mt: -2,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        '& .MuiButton-root': {
                            minWidth: 'auto',
                            px: 1,
                            py: 0.25,
                            fontSize: '0.6rem',
                            whiteSpace: 'nowrap',
                            backgroundColor: 'var(--joy-palette-neutral-100)',
                            color: 'var(--joy-palette-neutral-600)',
                        },
                        '& .MuiButton-root:hover': {
                            backgroundColor: 'var(--joy-palette-neutral-200)',
                        },
                        '& .MuiButton-root .MuiButton-startDecorator': {
                            display: 'none',
                        },
                        '& .MuiButton-root .MuiButton-endDecorator': {
                            marginLeft: '4px',
                        },
                        '& .MuiButton-root .MuiButton-endDecorator svg': {
                            fontSize: '0.75rem',
                        },
                    }}
                >
                    <SendLoadInfoButton
                        load={load}
                        truck={truck}
                        duration={duration}
                        deadheadMiles={calculateDeadheadMiles()}
                        rate={Number(load?.rateInfo?.rateUsd || 0)}
                    />
                </Box>
            )}
        </Box>
    )
}
